import { useToolMentionSource } from 'src/components/common/lexical-mentions/sources/useToolMentionSource';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Badge } from 'src/components/ui/badge';
import { Checkbox } from 'src/components/ui/checkbox';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Grid } from 'src/components/common/grid';
import { MarkdownWYSIWYGEditor } from 'src/components/common/MarkdownWYSIWYGEditor';
import { useAgentDefinitionForm } from './AgentDefinitionFormContext';
import type { AgentDefinitionResponse } from '@erp/rpc';
import { SystemEntity } from '@erp/shared-types';
import { msg, T, useGT, useMessages } from 'gt-react';
import { NamespaceField } from 'src/components/namespaces/NamespaceField';
import {
  unassignedNamespaceOption,
  namespaceIdFromSelectValue,
  selectValueFromNamespaceId,
} from 'src/components/namespaces/namespaceLabels';
import { useUpdateAgentDefinition } from 'src/api/rpc/hooks/agent-definitions/useAgentDefinitions';
import { useToast } from 'src/context/ToastContext';
import { FailureActionsSection } from './components/FailureActionsSection';

const SYSTEM_ENTITY_SOCKET_TYPE_SELECTOR_VALUE = 'system_entity';
const TYPED_SYSTEM_ENTITY_SOCKET_PREFIX = 'entity_reference:';

/** Encoded message from `msg()` (string at runtime; avoid ReturnType<typeof msg> union with string[]). */
type MsgLabel = string;

const PRIMITIVE_INPUT_SOCKET_TYPES: { value: string; label: MsgLabel }[] = [
  {
    value: 'string',
    label: msg('String', { context: 'agent input socket type' }),
  },
  {
    value: 'number',
    label: msg('Number', { context: 'agent input socket type' }),
  },
  {
    value: 'boolean',
    label: msg('Boolean', { context: 'agent input socket type' }),
  },
  { value: 'date', label: msg('Date', { context: 'agent input socket type' }) },
  { value: 'json', label: msg('JSON', { context: 'agent input socket type' }) },
  { value: 'uuid', label: msg('UUID', { context: 'agent input socket type' }) },
  { value: 'any', label: msg('Any', { context: 'agent input socket type' }) },
];

const REFERENCE_INPUT_SOCKET_TYPES: { value: string; label: MsgLabel }[] = [
  {
    value: 'entity_reference',
    label: msg('Entity Reference (generic)', {
      context: 'agent input socket type',
    }),
  },
  {
    value: 'array<entity_reference>',
    label: msg('Entity Reference[] (generic)', {
      context: 'agent input socket type',
    }),
  },
  {
    value: 'array<string>',
    label: msg('String[]', { context: 'agent input socket type' }),
  },
  {
    value: 'array<number>',
    label: msg('Number[]', { context: 'agent input socket type' }),
  },
];

const SYSTEM_ENTITY_INPUT_SOCKET_TYPES: { value: string; label: MsgLabel }[] = [
  {
    value: SYSTEM_ENTITY_SOCKET_TYPE_SELECTOR_VALUE,
    label: msg('System Entity', { context: 'agent input socket type' }),
  },
];

const SYSTEM_ENTITY_TYPE_OPTIONS: { value: SystemEntity; label: MsgLabel }[] = [
  {
    value: SystemEntity.CORE_MEMORY_BANK_NOTE,
    label: msg('Memory Bank Note', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.CORE_MEMORY_BANK_NOTE_USAGE,
    label: msg('Memory Bank Usage', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.ORGANIZATION,
    label: msg('Organization', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.CORE_DOCUMENT,
    label: msg('Document', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.CORE_DOCUMENT_OCR_RESULT,
    label: msg('OCR Result', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.AR_CUSTOMER,
    label: msg('Customer', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.AR_CUSTOMER_PRODUCT,
    label: msg('Product Mapping', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.AR_STAGED_SALES_ORDER,
    label: msg('Sales Order Draft', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.AR_SALES_ORDER,
    label: msg('Sales Order', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.AR_SALES_ORDER_LINE_ITEM,
    label: msg('Line Item', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.AR_INVOICE,
    label: msg('AR Invoice', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.AR_PAYMENT_APPLICATION,
    label: msg('Payment Application', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.AP_INVOICE,
    label: msg('Invoice', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.AP_INVOICE_LINE_ITEM,
    label: msg('Invoice Line Item', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.AP_SUPPLIER,
    label: msg('Supplier', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.INTERNAL_PURCHASE_ORDER,
    label: msg('Purchase Order', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM,
    label: msg('PO Line Item', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.PAYMENT,
    label: msg('Payment', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.FINANCIAL_TRANSACTION,
    label: msg('Transaction', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.CHARGE,
    label: msg('Charge', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.INBOUND_EMAIL,
    label: msg('Inbound Email', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.OUTBOUND_EMAIL,
    label: msg('Outbound Email', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.IM_TRANSFER_ORDER,
    label: msg('Transfer Order', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.IM_TRANSFER_ORDER_LINE_ITEM,
    label: msg('Transfer Order Line Item', {
      context: 'system entity type display name',
    }),
  },
  {
    value: SystemEntity.SCM_SHIPMENT,
    label: msg('Shipment', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.WORKFLOW_DEFINITION,
    label: msg('Workflow', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.WORKFLOW_RUN,
    label: msg('Workflow Run', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.STEP_RUN,
    label: msg('Step Run', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.TASK,
    label: msg('Task', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.AI_AGENT_RUN,
    label: msg('Agent Run', { context: 'system entity type display name' }),
  },
  {
    value: SystemEntity.EXTERNAL_SYSTEM_REFERENCE,
    label: msg('External Reference', {
      context: 'system entity type display name',
    }),
  },
];

const KNOWN_INPUT_SOCKET_TYPES = new Set([
  ...PRIMITIVE_INPUT_SOCKET_TYPES.map((option) => option.value),
  ...REFERENCE_INPUT_SOCKET_TYPES.map((option) => option.value),
]);

function getSystemEntitySocketType(entity: string): string {
  return `${TYPED_SYSTEM_ENTITY_SOCKET_PREFIX}${entity}`;
}

function getSystemEntityFromSocketType(socketType: string): string | null {
  if (!socketType.startsWith(TYPED_SYSTEM_ENTITY_SOCKET_PREFIX)) {
    return null;
  }

  const entityType = socketType.slice(TYPED_SYSTEM_ENTITY_SOCKET_PREFIX.length);
  return entityType.length > 0 ? entityType : null;
}

export function AgentDefinitionModelConfigSection() {
  const gt = useGT();
  const {
    agentForm: form,
    setAgentForm: setForm,
    isEditable,
  } = useAgentDefinitionForm();

  if (!form) return null;

  const stopType = form.modelConfig.stopType ?? 'MAX_STEPS';

  return (
    <fieldset
      disabled={!isEditable}
      className="space-y-4 max-w-3xl border-0 p-0 m-0 disabled:opacity-70"
    >
      <h3 className="text-lg font-semibold">
        <T context="agent model settings section">Model Configuration</T>
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>
            <T context="agent model setting">Model Tier</T>
          </Label>
          <Select
            value={form.modelConfig.model ?? 'balanced'}
            onValueChange={(value) =>
              setForm({
                ...form,
                modelConfig: {
                  ...form.modelConfig,
                  model:
                    value as AgentDefinitionResponse['modelConfig']['model'],
                },
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fast">
                <T context="model tier option">Fast</T>
              </SelectItem>
              <SelectItem value="balanced">
                <T context="model tier option">Balanced</T>
              </SelectItem>
              <SelectItem value="smart">
                <T context="model tier option">Smart</T>
              </SelectItem>
              <SelectItem value="reasoning">
                <T context="model tier option">Reasoning</T>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>
            <T context="agent model setting">Stop Condition</T>
          </Label>
          <Select
            value={stopType}
            onValueChange={(value) => {
              if (value !== 'MAX_STEPS' && value !== 'NO_MORE_TASKS') return;
              setForm({
                ...form,
                modelConfig: {
                  ...form.modelConfig,
                  stopType: value,
                },
              });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MAX_STEPS">
                <T context="stop type option">Max Steps</T>
              </SelectItem>
              <SelectItem value="NO_MORE_TASKS">
                <T context="stop type option">No More Tasks</T>
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {stopType === 'NO_MORE_TASKS' ? (
              <T context="stop type description">
                Agent loops until get_next_task returns no tasks (hard limit:
                1000 steps).
              </T>
            ) : (
              <T context="stop type description">
                Agent stops after the configured number of steps.
              </T>
            )}
          </p>
        </div>
        {stopType === 'MAX_STEPS' && (
          <div className="space-y-2">
            <Label htmlFor="maxSteps">
              <T context="agent model setting">Max Steps</T>
            </Label>
            <Input
              id="maxSteps"
              type="number"
              placeholder={gt('10', {
                context: 'default max steps number placeholder',
              })}
              value={form.modelConfig.maxSteps ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  modelConfig: {
                    ...form.modelConfig,
                    maxSteps: e.target.value
                      ? parseInt(e.target.value, 10)
                      : undefined,
                  },
                })
              }
            />
          </div>
        )}
      </div>
    </fieldset>
  );
}

function SocketTypeSelectItems({
  options,
  m,
}: {
  options: { value: string; label: MsgLabel }[];
  m: (descriptor: MsgLabel) => string;
}) {
  return options.map((option) => (
    <SelectItem key={option.value} value={option.value}>
      {m(option.label)}
    </SelectItem>
  ));
}

export function AgentDefinitionAgentTab() {
  const gt = useGT();
  const m = useMessages();
  const {
    definition,
    currentVersion,
    enabledTools,
    agentForm: form,
    setAgentForm: setForm,
    isEditable,
    isArchived,
  } = useAgentDefinitionForm();
  const updateAgent = useUpdateAgentDefinition();
  const { showToast } = useToast();

  // The web browser tool always runs as the A2A "Browser Operator" sub-agent —
  // mark it so its @-mention chip shows an agent icon. (React Compiler memoizes
  // this, so no useMemo is needed.)
  const subAgentToolTypes = enabledTools.includes('web_browser_execute')
    ? ['web_browser_execute']
    : [];

  const toolMentionSource = useToolMentionSource(
    enabledTools,
    subAgentToolTypes,
  );

  if (!definition || !form) return null;

  const agentDefinitionId = definition.id;

  function handleNamespaceChange(next: string) {
    updateAgent.mutate(
      {
        agentDefinitionId,
        namespaceId: namespaceIdFromSelectValue(next),
      },
      {
        onSuccess: () => {
          showToast({
            title: gt('Namespace updated', {
              context: 'toast after assigning an agent to a namespace',
            }),
            color: 'success',
          });
        },
        onError: (err) => {
          showToast({
            title: gt('Could not update namespace', {
              context: 'toast after agent namespace assignment fails',
            }),
            message: err.message,
            color: 'error',
          });
        },
      },
    );
  }

  function addInputField() {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        inputFields: [
          ...prev.inputFields,
          {
            fieldName: '',
            displayName: '',
            description: '',
            socketType: 'string',
            required: true,
            sortOrder: prev.inputFields.length,
          },
        ],
      };
    });
  }

  function removeInputField(index: number) {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        inputFields: prev.inputFields.filter((_, i) => i !== index),
      };
    });
  }

  function updateInputField(
    index: number,
    updater: (
      field: AgentDefinitionResponse['inputFields'][number],
    ) => AgentDefinitionResponse['inputFields'][number],
  ) {
    setForm((prev) => {
      if (!prev) return prev;
      const updated = [...prev.inputFields];
      const currentField = updated[index];
      if (!currentField) return prev;
      updated[index] = updater(currentField);

      return {
        ...prev,
        inputFields: updated,
      };
    });
  }

  function addOutputField() {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        outputFields: [
          ...prev.outputFields,
          {
            fieldName: '',
            displayName: '',
            description: '',
            socketType: 'string',
            sortOrder: prev.outputFields.length,
          },
        ],
      };
    });
  }

  function removeOutputField(index: number) {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        outputFields: prev.outputFields.filter((_, i) => i !== index),
      };
    });
  }

  return (
    <div className="space-y-3 min-w-0">
      <div className="max-w-3xl">
        <NamespaceField
          value={selectValueFromNamespaceId(definition.namespaceId)}
          onChange={handleNamespaceChange}
          disabled={isArchived || updateAgent.isPending}
          noneOption={unassignedNamespaceOption(gt)}
          label={<T context="agent definition field">Namespace</T>}
          description={
            <T context="hint under the agent namespace picker">
              Groups this agent with the workflows and tasks in the same module.
              Saved immediately.
            </T>
          }
        />
      </div>
      <fieldset
        disabled={!isEditable}
        className="space-y-3 min-w-0 border-0 p-0 m-0 disabled:opacity-70"
      >
        {/* Basic info */}
        <div className="space-y-4 max-w-3xl">
          <div className="space-y-2">
            <Label htmlFor="name">
              <T context="agent definition field">Name</T>
            </Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              <T context="agent definition field">Description</T>
            </Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="systemPrompt">
              <T context="agent definition field">System Prompt</T>
            </Label>
            <MarkdownWYSIWYGEditor
              key={`${definition.id}-${currentVersion?.id ?? definition.updatedAt}-system-prompt`}
              initialValue={form.systemPrompt}
              // The Lexical editor is contentEditable and isn't disabled by the
              // surrounding fieldset, so gate its editability explicitly.
              readonly={!isEditable}
              onChange={(content) =>
                setForm((prev) =>
                  prev ? { ...prev, systemPrompt: content } : prev,
                )
              }
              placeholder={gt(
                'Define behavior, constraints, and output style for this agent.',
                { context: 'system prompt editor placeholder' },
              )}
              height={280}
              showToolbar
              mentionSources={[toolMentionSource]}
            />
          </div>
        </div>

        <AgentDefinitionModelConfigSection />

        <FailureActionsSection
          config={form.config}
          onChange={(config) =>
            setForm((prev) => (prev ? { ...prev, config } : prev))
          }
        />

        {/* Template type (read-only) */}
        {definition.templateType && (
          <div className="space-y-2 max-w-3xl">
            <Label>
              <T context="read-only template type label">Template Type</T>
            </Label>
            <div>
              <Badge variant="outline">{definition.templateType}</Badge>
            </div>
          </div>
        )}

        {/* Input and Output fields side by side */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  <T context="agent IO fields section">Input Fields</T>
                </h3>
                <Button variant="outline" size="sm" onClick={addInputField}>
                  <PlusIcon className="h-4 w-4 mr-1" />
                  <T context="add input field button">Add</T>
                </Button>
              </div>
              {form.inputFields.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  <T context="empty input fields">No input fields defined.</T>
                </p>
              )}
              {form.inputFields.map((field, idx) => {
                const systemEntityType = getSystemEntityFromSocketType(
                  field.socketType,
                );
                const hasTypedSystemEntitySocketType =
                  systemEntityType !== null;
                const hasCustomInputSocketType =
                  field.socketType.length > 0 &&
                  !KNOWN_INPUT_SOCKET_TYPES.has(field.socketType) &&
                  !hasTypedSystemEntitySocketType;
                const selectedInputSocketType = hasTypedSystemEntitySocketType
                  ? SYSTEM_ENTITY_SOCKET_TYPE_SELECTOR_VALUE
                  : field.socketType.length > 0
                    ? field.socketType
                    : undefined;

                return (
                  <div
                    key={idx}
                    className="flex items-start gap-2 rounded-md border p-3"
                  >
                    <div className="grid grid-cols-1 gap-2 flex-1">
                      <Input
                        placeholder={gt('fieldName', {
                          context: 'technical field name placeholder',
                        })}
                        value={field.fieldName}
                        onChange={(e) => {
                          updateInputField(idx, (current) => ({
                            ...current,
                            fieldName: e.target.value,
                          }));
                        }}
                      />
                      <Input
                        placeholder={gt('Display Name', {
                          context: 'field display name placeholder',
                        })}
                        value={field.displayName}
                        onChange={(e) => {
                          updateInputField(idx, (current) => ({
                            ...current,
                            displayName: e.target.value,
                          }));
                        }}
                      />

                      <Select
                        value={selectedInputSocketType}
                        onValueChange={(value) => {
                          updateInputField(idx, (current) => ({
                            ...current,
                            socketType:
                              value === SYSTEM_ENTITY_SOCKET_TYPE_SELECTOR_VALUE
                                ? getSystemEntitySocketType(
                                    systemEntityType ??
                                      SYSTEM_ENTITY_TYPE_OPTIONS[0]?.value ??
                                      SystemEntity.AP_INVOICE,
                                  )
                                : value,
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={gt('Select input type', {
                              context: 'socket type select placeholder',
                            })}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>
                              <T context="socket type group">Primitive</T>
                            </SelectLabel>
                            <SocketTypeSelectItems
                              options={PRIMITIVE_INPUT_SOCKET_TYPES}
                              m={m}
                            />
                          </SelectGroup>

                          <SelectGroup>
                            <SelectLabel>
                              <T context="socket type group">References</T>
                            </SelectLabel>
                            <SocketTypeSelectItems
                              options={REFERENCE_INPUT_SOCKET_TYPES}
                              m={m}
                            />
                          </SelectGroup>

                          <SelectGroup>
                            <SelectLabel>
                              <T context="socket type group">System Entity</T>
                            </SelectLabel>
                            <SocketTypeSelectItems
                              options={SYSTEM_ENTITY_INPUT_SOCKET_TYPES}
                              m={m}
                            />
                          </SelectGroup>

                          {hasCustomInputSocketType && (
                            <SelectGroup>
                              <SelectLabel>
                                <T context="socket type group">Custom</T>
                              </SelectLabel>
                              <SelectItem value={field.socketType}>
                                {field.socketType}
                              </SelectItem>
                            </SelectGroup>
                          )}
                        </SelectContent>
                      </Select>

                      {selectedInputSocketType ===
                        SYSTEM_ENTITY_SOCKET_TYPE_SELECTOR_VALUE && (
                        <div className="space-y-2">
                          <Label>
                            <T context="system entity type label">
                              Entity Type
                            </T>
                          </Label>
                          <Select
                            value={systemEntityType ?? undefined}
                            onValueChange={(value) => {
                              updateInputField(idx, (current) => ({
                                ...current,
                                socketType: getSystemEntitySocketType(value),
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={gt('Select entity type', {
                                  context: 'entity type placeholder',
                                })}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {SYSTEM_ENTITY_TYPE_OPTIONS.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {m(option.label)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`input-field-required-${idx}`}
                          checked={field.required}
                          onCheckedChange={(checked) => {
                            updateInputField(idx, (current) => ({
                              ...current,
                              required: checked === true,
                            }));
                          }}
                        />
                        <Label
                          htmlFor={`input-field-required-${idx}`}
                          className="cursor-pointer text-xs"
                        >
                          <T context="required field checkbox label">
                            Required (uncheck for optional)
                          </T>
                        </Label>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeInputField(idx)}
                    >
                      <TrashIcon className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  <T context="agent IO fields section">Output Fields</T>
                </h3>
                <Button variant="outline" size="sm" onClick={addOutputField}>
                  <PlusIcon className="h-4 w-4 mr-1" />
                  <T context="add output field button">Add</T>
                </Button>
              </div>
              {form.outputFields.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  <T context="empty output fields">No output fields defined.</T>
                </p>
              )}
              {form.outputFields.map((field, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 rounded-md border p-3"
                >
                  <div className="grid grid-cols-1 gap-2 flex-1">
                    <Input
                      placeholder={gt('fieldName', {
                        context: 'technical field name placeholder',
                      })}
                      value={field.fieldName}
                      onChange={(e) => {
                        const updated = [...form.outputFields];
                        updated[idx] = {
                          ...updated[idx],
                          fieldName: e.target.value,
                        };
                        setForm({ ...form, outputFields: updated });
                      }}
                    />
                    <Input
                      placeholder={gt('Display Name', {
                        context: 'field display name placeholder',
                      })}
                      value={field.displayName}
                      onChange={(e) => {
                        const updated = [...form.outputFields];
                        updated[idx] = {
                          ...updated[idx],
                          displayName: e.target.value,
                        };
                        setForm({ ...form, outputFields: updated });
                      }}
                    />
                    <Input
                      placeholder={gt('Description', {
                        context: 'field description placeholder',
                      })}
                      value={field.description}
                      onChange={(e) => {
                        const updated = [...form.outputFields];
                        updated[idx] = {
                          ...updated[idx],
                          description: e.target.value,
                        };
                        setForm({ ...form, outputFields: updated });
                      }}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOutputField(idx)}
                  >
                    <TrashIcon className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </Grid>
        </Grid>
      </fieldset>
    </div>
  );
}
